import { Icon } from '@/features/common/Icon';
import type { FilterSearchProps } from '@/features/common/FilterPanel/types/FilterSearchProps';
import '@/styles/search-input.css';

/**
 * A FilterPanel's free-text field — the one item in the row that takes the room left over. It
 * applies when the panel is submitted (Enter or "Caută"), not per keystroke: a request per letter
 * would page the list around under the reader's fingers.
 *
 * Three things a caller can ask for, all off by default, so the listings that had this field before
 * render exactly what they rendered:
 *
 * `labelHidden` keeps the label for assistive technology and takes it off the screen, as
 * FilterSelect's does — the accessible name is still the label's own text, not the placeholder,
 * which a browser may stop exposing once something is typed.
 *
 * `icon` dresses the field in MUD's search-input block: the magnifier inside the field on the left,
 * and that block's own border, hover and focus ring. It is the same component the header and
 * /cautare use, so the look and the focus state come from `styles/search-input.css` rather than
 * from any caller's sheet.
 *
 * `onSubmit` is Enter, for a caller with no surrounding form to submit. A field inside FilterPanel
 * leaves it unset and lets the form handle Enter, so nothing fires twice.
 */
export function FilterSearch({
  id,
  label,
  placeholder,
  value,
  onChange,
  maxLength = 100,
  labelHidden = false,
  icon = false,
  onSubmit,
}: FilterSearchProps) {
  const field = (
    <input
      id={id}
      type="text"
      inputMode="search"
      enterKeyHint="search"
      autoComplete="off"
      className={icon ? 'input' : undefined}
      maxLength={maxLength}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={
        onSubmit &&
        ((e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          onSubmit();
        })
      }
    />
  );

  return (
    <div className="filter-panel__field filter-panel__search">
      <label
        className={labelHidden ? 'filter-panel__label visually-hidden' : 'filter-panel__label'}
        htmlFor={id}
      >
        {label}
      </label>
      {icon ? (
        <div className="search-input rectangular large">
          <span className="icon-search" aria-hidden="true">
            <Icon name="search" className="icon-search__glyph" />
          </span>
          {field}
        </div>
      ) : (
        field
      )}
    </div>
  );
}
